import type { WebSocketMessageData } from '@/types/ws/WebSocketMessageData.ts';
import { z } from 'zod';
import { syncTargets } from '@/types/sync/SyncTarget.ts';
import {
    SYNC_TO_DESKTOP_APP_KEYS,
    SYNC_TO_WEB_VIEW_KEYS,
    syncToDesktopAppKeys,
    syncToWebViewKeys,
} from '@/types/sync/SyncKeys.ts';
import { syncToastHandler } from '@/ws/handlers/sync/webView/SyncToastHandler.ts';
import { logger } from '@/lib/logger.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import { routeChangeHandler } from '@/ws/handlers/sync/webView/RouteChangeHandler.ts';
import { storeSyncClientHandler } from '@/ws/handlers/sync/webView/StoreSyncClientHandler.ts';
import { storeSyncSettingsHandler } from '@/ws/handlers/sync/webView/StoreSyncSettingsHandler.ts';
import { storeSyncPromptHandler } from '@/ws/handlers/sync/webView/StoreSyncPromptHandler.ts';
import { storeSyncSourceHandler } from '@/ws/handlers/sync/webView/StoreSyncSourceHandler.ts';
import { showSolvePreviewHandler } from '@/ws/handlers/sync/webView/ShowSolvePreviewHandler.ts';
import { syncScrollHandler } from '@/ws/handlers/sync/webView/SyncScrollHandler.ts';
import { startSessionHandler } from '@/ws/handlers/sync/desktopApp/StartSessionHandler.ts';

const SyncClientSyncDataSchema = z
    .object({
        id: z.string(),
        target: z.enum(syncTargets),
        key: z.string(),
        value: z.string(),
    })
    .refine(
        (data) => {
            if (data.target === 'web-view') {
                return syncToWebViewKeys.includes(data.key);
            } else if (data.target === 'desktop-app') {
                return syncToDesktopAppKeys.includes(data.key);
            }
        },
        {
            message: 'Sync key is not valid for sync target',
            path: ['key'],
        },
    );

const syncToWebViewHandlerMap: Record<string, (data: object) => void> = {
    [SYNC_TO_WEB_VIEW_KEYS.toast]: syncToastHandler,
    [SYNC_TO_WEB_VIEW_KEYS.routeChange]: routeChangeHandler,
    [SYNC_TO_WEB_VIEW_KEYS.scroll]: syncScrollHandler,
    [SYNC_TO_WEB_VIEW_KEYS.showSolvePreview]: showSolvePreviewHandler,
    [SYNC_TO_WEB_VIEW_KEYS.storeSyncClient]: storeSyncClientHandler,
    [SYNC_TO_WEB_VIEW_KEYS.storeSyncSettings]: storeSyncSettingsHandler,
    [SYNC_TO_WEB_VIEW_KEYS.storeSyncPrompt]: storeSyncPromptHandler,
    [SYNC_TO_WEB_VIEW_KEYS.storeSyncSource]: storeSyncSourceHandler,
};

const syncToDesktopAppHandlerMap: Record<string, (data: object) => void> = {
    [SYNC_TO_DESKTOP_APP_KEYS.startSession]: startSessionHandler,
};

export const syncClientSyncMessageHandler = (messageData: WebSocketMessageData) => {
    const data = SyncClientSyncDataSchema.parse(messageData);

    let parsedValue;
    try {
        parsedValue = JSON.parse(data.value);
    } catch (e) {
        throw new Error('Invalid JSON: ' + (e as Error).message);
    }

    const sessionStore = useSessionStore();
    if (sessionStore.isInsideWebView && data.target === 'web-view') {
        if (syncToWebViewHandlerMap[data.key] === undefined) {
            logger.warn('Sync handler (web view) for key not found: ' + data.key);

            return;
        }

        syncToWebViewHandlerMap[data.key](parsedValue);
    } else if (!sessionStore.isInsideWebView && data.target === 'desktop-app') {
        if (syncToDesktopAppHandlerMap[data.key] === undefined) {
            logger.warn('Sync handler (desktop app) for key not found: ' + data.key);

            return;
        }

        syncToDesktopAppHandlerMap[data.key](parsedValue);
    }
};
